import { motion } from "framer-motion";
import { Compass, Leaf, Heart, Lightbulb } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import Logo3D from "@/components/Logo3D";
import logo from "@/assets/logo.webp";

const principles = [
  { icon: Compass, title: "Purposeful Design", desc: "Every space we design serves a clear purpose, blending aesthetics with function to create environments that enhance daily life." },
  { icon: Leaf, title: "Sustainable Practice", desc: "We integrate sustainable materials and energy-efficient solutions, ensuring that our buildings contribute positively to the environment." },
  { icon: Heart, title: "Human-Centered", desc: "Architecture should serve people first. We listen deeply to our clients and communities to create spaces that resonate emotionally." },
  { icon: Lightbulb, title: "Innovation & Craft", desc: "We combine cutting-edge technology with traditional craftsmanship, pushing boundaries while honouring architectural heritage." },
];

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const Philosophy = () => {
  return (
    <Layout transparentNav>
      <SEOHead
        title="Philosophy — Purposeful Architecture by Registered Architects"
        description="Dhivisha Architects' design philosophy: purposeful design, sustainable practice & human-centered architecture by COA-registered architects in Kumbakonam, Cauvery Delta region."
        path="/philosophy"
      />
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1431576901776-e539bd916ba2?w=1200&q=70" alt="Architectural details" className="w-full h-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
          <div className="absolute inset-0 bg-secondary/70" />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">Our Philosophy</h1>
          <p className="text-lg text-primary-foreground/80">About Architecture — What We Strive to Create</p>
        </motion.div>
      </section>

      {/* Statement with watermark logo */}
      <section className="py-20 relative overflow-hidden">
        <motion.img
          src={logo}
          alt=""
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] object-contain opacity-[0.04] pointer-events-none"
          animate={{ scale: [1, 1.05, 1], rotate: [0, 3, 0, -3, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <Logo3D size="lg" animate glow className="mx-auto mb-6" />
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-2xl md:text-3xl font-bold text-foreground leading-relaxed italic"
          >
            "Architecture is not just about building walls — it's about breaking barriers between spaces, people, and possibilities."
          </motion.blockquote>
          <motion.p
            className="text-muted-foreground mt-6 text-lg"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Our perspective on architecture, what we strive to create, and how our services contribute to society.
          </motion.p>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 bg-muted">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2
            className="text-3xl font-bold text-center text-foreground mb-14"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Core Principles
          </motion.h2>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {principles.map((item) => (
              <motion.div
                key={item.title}
                variants={staggerItem}
                whileHover={{ y: -4 }}
                className="flex gap-6 p-8 bg-card rounded border border-border hover:border-primary transition-all"
              >
                <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
                  <item.icon size={36} className="text-primary shrink-0 mt-1" />
                </motion.div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            className="rounded overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80" alt="Design process" className="w-full h-[400px] object-cover" loading="lazy" decoding="async" />
          </motion.div>
          <div>
            <motion.h2
              className="text-3xl font-bold text-foreground mb-6"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              Our Approach
            </motion.h2>
            <div className="space-y-4">
              {["Listen & Understand", "Conceptualize & Design", "Refine & Detail", "Build & Deliver"].map((step, i) => (
                <motion.div
                  key={step}
                  className="flex items-center gap-4"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.3 }}
                >
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">{i + 1}</span>
                  <span className="text-foreground font-medium">{step}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Philosophy;
